import { Icon } from "@iconify/react";
import type { RowSelectionState, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { type TicketDto, TicketPriority, TicketStatus } from "@/api/tickets";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TicketListSkeletonRows } from "@/features/tickets/components/ticket-list-skeleton-rows";
import { formatDateTime, formatShortTicketNumber, priorityPresentation, statusPresentation } from "@/features/tickets/utils/ticket-presentation";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";

type Props = {
  rows: TicketDto[];
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
};

/** 嵌入列表的表格仅提供查看入口，保留完整工作台的列与选择布局。 */
export function EmbeddedTicketListTable({ rows, sorting, onSortingChange }: Props) {
  const { intlLocale, t } = useI18n();
  const location = useLocation();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  const columns = useMemo<ReadonlyArray<DataTableColumnDef<TicketDto>>>(
    () => [
      {
        id: "select",
        header: ({ table }) => {
          const allSelected = table.getIsAllRowsSelected();
          const someSelected = table.getIsSomeRowsSelected();
          return (
            <Checkbox
              aria-label={t("tickets.list.selectAll")}
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={(checked) => table.toggleAllRowsSelected(checked === true)}
            />
          );
        },
        cell: ({ row }) => (
          <Checkbox
            aria-label={t("tickets.list.selectTicket", { number: row.original.number })}
            checked={row.getIsSelected()}
            onCheckedChange={(checked) => row.toggleSelected(checked === true)}
          />
        ),
        enableSorting: false,
        meta: { headerClassName: "w-10 pl-4", cellClassName: "w-10 pl-4" },
      },
      {
        accessorKey: "priority",
        header: t("tickets.fields.subject"),
        cell: ({ row }) => {
          const ticket = row.original;
          const status = statusPresentation(ticket.status);
          const priority = priorityPresentation(ticket.priority);
          const projectLabel = ticket.projectName ?? ticket.projectCode ?? t("tickets.project.unspecified");
          const updatedAt = ticket.lastModificationTime ?? ticket.creationTime;
          const updatedLabel = formatDateTime(updatedAt, intlLocale);
          const messageCount = new Intl.NumberFormat(intlLocale).format(ticket.messageCount ?? 0);
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Icon
                    aria-label={t("tickets.priority.ariaLabel", { priority: t(priority.labelKey) })}
                    className={cn("size-4 shrink-0", priority.className)}
                    icon={priority.icon}
                  />
                </TooltipTrigger>
                <TooltipContent>{t(priority.labelKey)}</TooltipContent>
              </Tooltip>
              <div className="min-w-0">
                <Link
                  className={cn(
                    "block min-w-0 truncate text-left text-sm leading-4 font-medium text-foreground hover:text-primary hover:underline",
                    ticket.status === TicketStatus.Cancelled && "line-through text-muted-foreground hover:text-muted-foreground",
                    ticket.priority === TicketPriority.Urgent && "text-destructive hover:text-destructive",
                  )}
                  title={ticket.subject}
                  to={{ pathname: `/embedded/tickets/${ticket.id}`, search: location.search }}
                  state={{ returnTo: `${location.pathname}${location.search}` }}
                >
                  {ticket.subject}
                </Link>
                <div className="mt-1.5 flex min-w-0 items-center gap-3 overflow-hidden text-xs text-muted-foreground">
                  <Badge className={cn("h-5 px-1.5", status.className)} variant="secondary">
                    <Icon icon={status.icon} />
                    <span className="shrink-0 text-xs font-medium">{t(status.labelKey)}</span>
                  </Badge>
                  <span className="shrink-0 font-mono" title={ticket.number}>
                    {formatShortTicketNumber(ticket.number)}
                  </span>
                  <Badge className="max-w-36 truncate rounded-sm px-1.5 font-normal text-muted-foreground" title={projectLabel} variant="outline">
                    {projectLabel}
                  </Badge>
                  <span className="inline-flex shrink-0 items-center gap-1" title={t("tickets.list.messageCount", { count: messageCount })}>
                    <Icon className="size-3.5" icon="tabler:message-dots" />
                    <span>{messageCount}</span>
                  </span>
                  <time className="min-w-0 truncate" dateTime={updatedAt} title={t("tickets.list.lastUpdated", { time: updatedLabel })}>
                    {t("tickets.list.lastUpdated", { time: updatedLabel })}
                  </time>
                </div>
              </div>
            </div>
          );
        },
        meta: { headerClassName: "pl-2", cellClassName: "min-w-0 whitespace-normal py-3 pl-2" },
        sortFn: "basic",
      },
      {
        id: "category",
        header: t("tickets.fields.category"),
        cell: ({ row }) =>
          row.original.categoryId ? (
            (row.original.categoryPath ?? row.original.categoryName)
          ) : (
            <span className="text-xs text-muted-foreground underline decoration-dashed">{t("tickets.category.uncategorized")}</span>
          ),
        enableSorting: false,
        meta: { headerClassName: "hidden w-64 xl:table-cell", cellClassName: "hidden whitespace-normal text-sm xl:table-cell" },
      },
      {
        id: "assignment",
        header: t("tickets.fields.assignment"),
        cell: ({ row }) => {
          const ticket = row.original;
          if (ticket.assignee)
            return (
              <div className="flex items-center gap-2 whitespace-nowrap">
                <Avatar size="sm">
                  {ticket.assignee.avatarUrl ? <AvatarImage alt="" src={ticket.assignee.avatarUrl} /> : null}
                  <AvatarFallback>{ticket.assignee.displayName.slice(0, 1)}</AvatarFallback>
                </Avatar>
                <span className="truncate">{ticket.assignee.displayName}</span>
              </div>
            );
          if (ticket.assignedTeamId)
            return (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Avatar size="sm">
                  <AvatarFallback>
                    <Icon aria-hidden="true" icon="lucide:users-round" className="size-3" />
                  </AvatarFallback>
                </Avatar>
                <span className="truncate text-xs underline decoration-dashed">{ticket.assignedTeamName}</span>
              </div>
            );
          return <span className="text-muted-foreground">{t("tickets.assignment.pending")}</span>;
        },
        enableSorting: false,
        meta: { headerClassName: "hidden w-48 md:table-cell", cellClassName: "hidden md:table-cell" },
      },
      {
        accessorKey: "creationTime",
        header: t("tickets.fields.creationTime"),
        cell: ({ row }) => <time dateTime={row.original.creationTime}>{formatDateTime(row.original.creationTime, intlLocale)}</time>,
        meta: { headerClassName: "hidden w-40 xl:table-cell", cellClassName: "hidden whitespace-nowrap text-muted-foreground xl:table-cell" },
        sortFn: "datetime",
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("tickets.fields.actions")}</span>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button aria-label={t("tickets.list.ticketActions", { number: row.original.number })} size="icon-sm" variant="ghost">
                <Icon icon="tabler:dots" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                  <Link
                    to={{ pathname: `/embedded/tickets/${row.original.id}`, search: location.search }}
                    state={{ returnTo: `${location.pathname}${location.search}` }}
                  >
                    <Icon icon="tabler:external-link" />
                    {t("tickets.actions.view")}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        enableSorting: false,
        meta: { headerClassName: "w-12", cellClassName: "pr-3 text-right" },
      },
    ],
    [intlLocale, location.pathname, location.search, t],
  );

  return (
    <TooltipProvider>
      <DataTable
        columnPinning={{ start: ["select"], end: ["actions"] }}
        columns={columns}
        loadingRows={<TicketListSkeletonRows />}
        data={rows}
        enableRowSelection
        getRowId={(ticket) => ticket.id}
        manualSorting
        rowSelection={rowSelection}
        sorting={sorting}
        tableClassName="table-fixed"
        onRowSelectionChange={setRowSelection}
        onSortingChange={(updater) => onSortingChange(typeof updater === "function" ? updater(sorting) : updater)}
      />
      {selectedCount > 0 ? (
        <div className="flex items-center justify-between border-t px-4 py-2 text-sm text-muted-foreground">
          <span>{t("tickets.bulk.selectedCount", { count: new Intl.NumberFormat(intlLocale).format(selectedCount) })}</span>
          <Button size="sm" variant="ghost" onClick={() => setRowSelection({})}>
            {t("tickets.bulk.clearSelection")}
          </Button>
        </div>
      ) : null}
    </TooltipProvider>
  );
}
